import AddIcon from '@mui/icons-material/Add'
import ApartmentIcon from '@mui/icons-material/Apartment'
import BuildIcon from '@mui/icons-material/Build'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import LinkIcon from '@mui/icons-material/Link'
import CloseIcon from '@mui/icons-material/Close'
import ReceiptIcon from '@mui/icons-material/Receipt'
import BusinessIcon from '@mui/icons-material/Business'
import CategoryIcon from '@mui/icons-material/Category'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import PsychologyIcon from '@mui/icons-material/Psychology'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Expense, ExpenseCategory, RepairSubcategory, Vehicle, computeExpenseAmount, type ExpenseDiscountType } from '@otw/models'
import dayjs from 'dayjs'
import { useDashboardActiveInventory } from 'hooks/dashboard'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useExpenses, isValidExpenseCategory } from 'hooks/useExpenses'
import { formatPrice } from 'core/utils/inventoryUtils'
import { z } from 'zod'
import { useRepairShopLocations, filterRepairShopsForPicker } from 'hooks/inventory/useRepairShopLocations'
import { filterVehicleLinkOptions } from 'hooks/inventory/filterVehicleLinkOptions'
import useAdminTransporter from 'hooks/admin/useAdminTransporter'
import { useExpenseSubcategories } from 'hooks/useExpenseSubcategories'
import { useRepairCategories } from 'hooks/useRepairCategories'
import {
  RepairCategoryPicker,
  RepairTaxonomyDialog,
} from '@/components/adminDashboard'

// Define schema for validating extracted invoice data
export const invoiceDataSchema = z.object({
  businessName: z.string().optional(),
  invoiceNumber: z.string().optional(),
  date: z.string().optional(),
  amount: z.number().optional(),
  description: z.string().optional(),
  category: z.string().optional(),
  customCategory: z.string().optional(),
});

export type ExtractedInvoiceData = z.infer<typeof invoiceDataSchema>;

// Helper function to validate and clean extracted data
export const validateExtractedInvoiceData = (data: any): ExtractedInvoiceData => {
  try {
    // Parse and validate using Zod schema
    const validatedData = invoiceDataSchema.parse(data);
    return validatedData;
  } catch (error) {
    console.error('Invoice data validation error:', error);
    // Return an empty object that matches the schema
    return {};
  }
};

// Function to map extracted data to expense format
export const mapExtractedDataToExpense = (data: ExtractedInvoiceData) => {
  let category: ExpenseCategory = 'Other';
  let customCategory: string | null = null;
  
  // Try to map the extracted category to a valid ExpenseCategory
  if (data.category && isValidExpenseCategory(data.category)) {
    category = data.category as ExpenseCategory;
  } else if (data.category) {
    // If not a valid category, use "Other" and set customCategory
    customCategory = data.category;
  }
  
  return {
    category,
    customCategory,
    amount: data.amount ?? 0,
    description: data.description ?? '',
    date: data.date ? new Date(data.date) : new Date(),
    invoiceNumber: data.invoiceNumber ?? null,
    businessName: data.businessName ?? '',
  };
};

// ExpenseDialog component
interface ExpenseDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (
    expense: Omit<Expense, 'created_by' | 'created_at' | 'updated_at' | 'updated_by'>,
    attachmentFiles?: File[] | null,
  ) => Promise<void>
  editMode?: boolean
  expenseToEdit?: Expense | null
}

const ExpenseDialog: React.FC<ExpenseDialogProps> = ({ open, onClose, onSubmit, editMode = false, expenseToEdit = null }) => {
  const [formData, setFormData] = useState<{
    category: ExpenseCategory
    amount: string
    description: string
    date: Date
    customCategory: string | null
    vehicleId: string | null
    transportCompanyId: string | null
    selectedVehicleInfo: Vehicle | null
    invoiceNumber: string | null
    vendorCategory: string | null
    businessName: string
    attachmentPath: string | null
    attachmentURL: string | null
    repairSubcategories: RepairSubcategory[]
    customRepairSubcategory: string
    repairPartsAmount: string
    repairLaborAmount: string
    repairFeesAmount: string
    repairTaxAmount: string
    repairShopId: string | null
    paid: boolean
    creditCardCharge: string
    discountType: '' | 'percent' | 'amount'
    discountValue: string
  }>({
    category: 'Shipping',
    amount: '',
    description: '',
    date: new Date(),
    paid: false,
    creditCardCharge: '',
    discountType: '',
    discountValue: '',
    customCategory: null,
    vehicleId: null,
    transportCompanyId: null,
    selectedVehicleInfo: null,
    invoiceNumber: null,
    vendorCategory: null,
    businessName: '',
    attachmentPath: null,
    attachmentURL: null,
    repairSubcategories: [],
    customRepairSubcategory: '',
    repairPartsAmount: '',
    repairLaborAmount: '',
    repairFeesAmount: '',
    repairTaxAmount: '',
    repairShopId: null,
  })
  const [formError, setFormError] = useState<string | null>(null)
  const [taxonomyDialogOpen, setTaxonomyDialogOpen] = useState(false)
  const { categories: repairCategories } = useRepairCategories()
  const { subcategories: customSubcategories } = useExpenseSubcategories()
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<Array<{ path: string | null; url: string }>>([]);
  const [attachmentPreviewURL, setAttachmentPreviewURL] = useState<string | null>(null);
  const isMobile = useIsMobile()

  // Get expenses hook for extraction
  const onExtractionStart = useCallback(() => {
    setFormError(null)
  }, [])

  const onExtractionSuccess = useCallback((extractedData: ExtractedInvoiceData) => {
    setFormData(prev => {
      const mappedData = mapExtractedDataToExpense(extractedData)

      const updates: Partial<typeof formData> = {
        category: mappedData.category,
        customCategory: mappedData.customCategory,
        description: mappedData.description || prev.description,
        businessName: mappedData.businessName || prev.businessName,
        invoiceNumber: mappedData.invoiceNumber,
      }

      if (mappedData.amount) {
        updates.amount = mappedData.amount.toString()
      }

      if (mappedData.date) {
        updates.date = mappedData.date
      }

      return { ...prev, ...updates }
    })
  }, [])

  const onExtractionError = useCallback((error: Error) => {
    setFormError(`AI Extraction Failed: ${error.message}. Please fill manually.`)
  }, [])

  const { extractFromAttachment, isExtracting } = useExpenses({
    options: {
      onExtractionStart,
      onExtractionSuccess,
      onExtractionError,
    },
  })

  // Get vehicles for linking to expenses
  const { data: vehicles = [], isLoading: vehiclesLoading } = useDashboardActiveInventory(open)
  const { repairShopLocations, isLoading: repairShopLocationsLoading } = useRepairShopLocations()
  const [includeInactiveRepairShops, setIncludeInactiveRepairShops] = useState(false)
  const repairShopPickerOptions = useMemo(
    () =>
      filterRepairShopsForPicker(repairShopLocations, {
        includeInactive: includeInactiveRepairShops,
      }),
    [repairShopLocations, includeInactiveRepairShops],
  )
  const { transporterGroups, isLoading: transporterGroupsLoading } = useAdminTransporter()
  
  // Initialize form data when the dialog opens or expenseToEdit changes
  useEffect(() => {
    if (open && editMode && expenseToEdit) {
      setFormData({
        category: expenseToEdit.category as ExpenseCategory,
        amount: expenseToEdit.amount.toString(),
        description: expenseToEdit.description,
        date: expenseToEdit.date instanceof Date 
          ? expenseToEdit.date 
          : new Date(expenseToEdit.date),
        customCategory: expenseToEdit.customCategory,
        vehicleId: expenseToEdit.vehicleId,
        transportCompanyId: expenseToEdit.transportCompanyId || null,
        selectedVehicleInfo: null, // Will be set in the next effect
        invoiceNumber: expenseToEdit.invoiceNumber,
        vendorCategory: expenseToEdit.vendorCategory,
        businessName: expenseToEdit.businessName || '',
        attachmentPath: expenseToEdit.attachmentPath || null,
        attachmentURL: expenseToEdit.attachmentURL || null,
        repairSubcategories: expenseToEdit.repairSubcategories || [],
        customRepairSubcategory: expenseToEdit.customRepairSubcategory || '',
        repairPartsAmount: expenseToEdit.repairPartsAmount ? expenseToEdit.repairPartsAmount.toString() : '',
        repairLaborAmount: expenseToEdit.repairLaborAmount ? expenseToEdit.repairLaborAmount.toString() : '',
        repairFeesAmount: expenseToEdit.repairFeesAmount ? expenseToEdit.repairFeesAmount.toString() : '',
        repairTaxAmount: expenseToEdit.repairTaxAmount ? expenseToEdit.repairTaxAmount.toString() : '',
        repairShopId: expenseToEdit.repairShopId || null,
        paid: expenseToEdit.paid === true,
        creditCardCharge:
          expenseToEdit.creditCardCharge != null && !Number.isNaN(expenseToEdit.creditCardCharge)
            ? String(expenseToEdit.creditCardCharge)
            : '',
        discountType:
          expenseToEdit.discountType === 'percent' || expenseToEdit.discountType === 'amount'
            ? expenseToEdit.discountType
            : '',
        discountValue:
          expenseToEdit.discountValue != null && !Number.isNaN(expenseToEdit.discountValue)
            ? String(expenseToEdit.discountValue)
            : '',
      });
      setAttachmentFiles([]); // Reset file input
      const existingUrls = Array.isArray(expenseToEdit.attachmentURLs)
        ? expenseToEdit.attachmentURLs
        : expenseToEdit.attachmentURL
          ? [expenseToEdit.attachmentURL]
          : []
      const existingPaths = Array.isArray(expenseToEdit.attachmentPaths)
        ? expenseToEdit.attachmentPaths
        : expenseToEdit.attachmentPath
          ? [expenseToEdit.attachmentPath]
          : []
      setExistingAttachments(
        existingUrls.map((url, index) => ({ url, path: existingPaths[index] ?? existingPaths[0] ?? null })),
      )
      setAttachmentPreviewURL(existingUrls[0] ?? null)
    } else if (open && !editMode) {
      // Reset form for new expense
      setFormData({
        category: 'Shipping',
        amount: '',
        description: '',
        date: new Date(),
        customCategory: null,
        vehicleId: null,
        transportCompanyId: null,
        selectedVehicleInfo: null,
        invoiceNumber: null,
        vendorCategory: null,
        businessName: '',
        attachmentPath: null,
        attachmentURL: null,
        repairSubcategories: [],
        customRepairSubcategory: '',
        repairPartsAmount: '',
        repairLaborAmount: '',
        repairFeesAmount: '',
        repairTaxAmount: '',
        repairShopId: null,
        paid: false,
        creditCardCharge: '',
        discountType: '',
        discountValue: '',
      });
      setAttachmentFiles([]);
      setExistingAttachments([]);
      setAttachmentPreviewURL(null);
    }
  }, [open, editMode, expenseToEdit]);

  // Set selected vehicle info when vehicles are loaded and we have a vehicleId
  useEffect(() => {
    if (!formData.vehicleId || !vehicles || vehicles.length === 0) return
    const selectedVehicle = vehicles.find(v => v.id === formData.vehicleId) || null
    if (!selectedVehicle) return
    setFormData(prev => {
      if (prev.selectedVehicleInfo?.id === selectedVehicle.id) return prev
      return {
        ...prev,
        selectedVehicleInfo: selectedVehicle,
      }
    })
  }, [formData.vehicleId, vehicles])

  // Handle form input changes for text fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    if (name === 'amount' || name === 'creditCardCharge' || name === 'discountValue') {
      // Only allow numbers and decimal point
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setFormData(prev => {
          const next = { ...prev, [name]: value }
          if (prev.category === 'Repairs') {
            const parts = parseFloat(next.repairPartsAmount) || 0
            const labor = parseFloat(next.repairLaborAmount) || 0
            const fees = parseFloat(next.repairFeesAmount) || 0
            const tax = parseFloat(next.repairTaxAmount) || 0
            const discountType =
              next.discountType === 'percent' || next.discountType === 'amount' ? next.discountType : null
            const discountValue =
              discountType && next.discountValue.trim() !== ''
                ? parseFloat(next.discountValue)
                : null
            const cc =
              next.creditCardCharge.trim() === '' ? 0 : parseFloat(next.creditCardCharge) || 0
            const total = computeExpenseAmount({
              subtotal: parts + labor + fees + tax,
              discountType,
              discountValue:
                discountValue != null && !Number.isNaN(discountValue) && discountValue > 0
                  ? discountType === 'percent'
                    ? Math.min(100, discountValue)
                    : discountValue
                  : null,
              creditCardCharge: cc > 0 ? cc : null,
            })
            next.amount = total > 0 ? total.toString() : ''
          }
          return next
        })
      }
    } else if (
      name === 'repairPartsAmount' ||
      name === 'repairLaborAmount' ||
      name === 'repairFeesAmount' ||
      name === 'repairTaxAmount'
    ) {
      // Handle repair amounts and auto-calculate total
      // Only allow numbers and decimal point
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setFormData(prev => {
          const parts = name === 'repairPartsAmount' ? (parseFloat(value) || 0) : (parseFloat(prev.repairPartsAmount) || 0)
          const labor = name === 'repairLaborAmount' ? (parseFloat(value) || 0) : (parseFloat(prev.repairLaborAmount) || 0)
          const fees = name === 'repairFeesAmount' ? (parseFloat(value) || 0) : (parseFloat(prev.repairFeesAmount) || 0)
          const tax = name === 'repairTaxAmount' ? (parseFloat(value) || 0) : (parseFloat(prev.repairTaxAmount) || 0)
          const discountType =
            prev.discountType === 'percent' || prev.discountType === 'amount' ? prev.discountType : null
          const rawDiscount = discountType && prev.discountValue.trim() !== '' ? parseFloat(prev.discountValue) : null
          const discountValue =
            rawDiscount != null && !Number.isNaN(rawDiscount) && rawDiscount > 0
              ? discountType === 'percent'
                ? Math.min(100, rawDiscount)
                : rawDiscount
              : null
          const cc =
            prev.creditCardCharge.trim() === '' ? 0 : parseFloat(prev.creditCardCharge) || 0
          const total = computeExpenseAmount({
            subtotal: parts + labor + fees + tax,
            discountType,
            discountValue,
            creditCardCharge: cc > 0 ? cc : null,
          })
          return {
            ...prev,
            [name]: value,
            amount: total > 0 ? total.toString() : '',
          }
        })
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  // Handle select changes
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target
    
    // Allow linking vehicles for ANY expense category, not just Repairs
    setFormData(prev => {
      const next = {
        ...prev,
        [name]: value,
        // Reset customCategory when changing category away from "Other"
        ...(name === 'category' && value !== 'Other' ? { customCategory: null } : {}),
        // Clear repair subcategories, amounts, and repair shop when changing category away from "Repairs"
        ...(name === 'category' && value !== 'Repairs' ? { 
          repairSubcategories: [],
          customRepairSubcategory: '',
          repairPartsAmount: '',
          repairLaborAmount: '',
          repairFeesAmount: '',
          repairTaxAmount: '',
          repairShopId: null,
          amount: '',
        } : {}),
        ...(name === 'category' && value !== 'Shipping' ? {
          transportCompanyId: null,
        } : {}),
        ...(name === 'discountType' && !value ? { discountValue: '' } : {}),
      } as typeof prev

      if (
        (name === 'discountType' || (prev.category === 'Repairs' && name === 'category')) &&
        (next.category === 'Repairs' || prev.category === 'Repairs')
      ) {
        const parts = parseFloat(next.repairPartsAmount) || 0
        const labor = parseFloat(next.repairLaborAmount) || 0
        const fees = parseFloat(next.repairFeesAmount) || 0
        const tax = parseFloat(next.repairTaxAmount) || 0
        const discountType =
          next.discountType === 'percent' || next.discountType === 'amount' ? next.discountType : null
        const rawDiscount = discountType && next.discountValue.trim() !== '' ? parseFloat(next.discountValue) : null
        const discountValue =
          rawDiscount != null && !Number.isNaN(rawDiscount) && rawDiscount > 0
            ? discountType === 'percent'
              ? Math.min(100, rawDiscount)
              : rawDiscount
            : null
        const cc = next.creditCardCharge.trim() === '' ? 0 : parseFloat(next.creditCardCharge) || 0
        if (next.category === 'Repairs') {
          const total = computeExpenseAmount({
            subtotal: parts + labor + fees + tax,
            discountType,
            discountValue,
            creditCardCharge: cc > 0 ? cc : null,
          })
          next.amount = total > 0 ? total.toString() : ''
        }
      }
      return next
    })
  }

  // Handle repair subcategory toggle
  const handleSubcategoryToggle = (subcategory: RepairSubcategory) => {
    setFormData(prev => {
      const current = prev.repairSubcategories || []
      const isSelected = current.includes(subcategory)
      return {
        ...prev,
        repairSubcategories: isSelected
          ? current.filter(s => s !== subcategory)
          : [...current, subcategory],
        // Clear custom repair subcategory if "Other" is deselected
        customRepairSubcategory: isSelected && subcategory === 'Other' ? '' : prev.customRepairSubcategory
      }
    })
  }

  // Handle date change - ensure we don't change the time component if not necessary
  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      setFormData(prev => {
        // If in edit mode and we have an existing date, try to preserve the time component
        if (editMode && expenseToEdit && prev.date instanceof Date) {
          const newDate = date.toDate();
          // Only modify the date part, leave time intact if possible
          const existingDate = prev.date;
          newDate.setHours(existingDate.getHours());
          newDate.setMinutes(existingDate.getMinutes());
          newDate.setSeconds(existingDate.getSeconds());
          newDate.setMilliseconds(existingDate.getMilliseconds());
          
          return {
            ...prev,
            date: newDate
          };
        }
        
        // Otherwise just use the new date
        return {
          ...prev,
          date: date.toDate(),
        };
      });
    }
  }

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) {
      setAttachmentFiles([]);
      setAttachmentPreviewURL(existingAttachments[0]?.url ?? null);
      return;
    }
    setAttachmentFiles(prev => [...prev, ...files]);
    if (files.length > 0) {
      const firstFile = files[0]
      // Create a preview URL for image files
      if (firstFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setAttachmentPreviewURL(reader.result as string);
        };
        reader.readAsDataURL(firstFile);
      } else {
        setAttachmentPreviewURL(null); // Not an image, no preview
      }
      // Update formData to reflect that a new file is staged for upload,
      // but clear existing attachmentPath/URL as they will be replaced.
      setFormData(prev => ({
        ...prev,
        attachmentPath: null, // Will be set on upload
        attachmentURL: null,  // Will be set on upload
      }));
    } else {
      setAttachmentPreviewURL(null);
      // If file is removed, and we are in edit mode with an existing attachment,
      // revert to showing the existing attachment.
      if (editMode && existingAttachments.length > 0) {
        setAttachmentPreviewURL(existingAttachments[0].url);
        setFormData(prev => ({
          ...prev,
          attachmentPath: existingAttachments[0].path,
          attachmentURL: existingAttachments[0].url,
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          attachmentPath: null,
          attachmentURL: null,
        }));
      }
    }
  };

  // Callback for initiating extraction
  const handleExtractInvoiceData = useCallback(async () => {
    const firstAttachment = attachmentFiles[0]
    if (firstAttachment) {
      try {
        await extractFromAttachment(firstAttachment);
        // Success is handled by onExtractionSuccess in useExpenses options
      } catch (error) {
        // Error is handled by onExtractionError in useExpenses options
        console.error("Extraction initiation failed locally:", error); // Should be rare
      }
    }
  }, [attachmentFiles, extractFromAttachment]);

  // Handle vehicle selection
  const handleVehicleChange = (_: any, vehicle: Vehicle | null) => {
    if (vehicle) {
      setFormData(prev => ({
        ...prev,
        vehicleId: vehicle.id,
        selectedVehicleInfo: vehicle,
        // Don't clear attachment data when selecting a vehicle
        // invoiceNumber: null,
        // vendorCategory: null,
        // businessName: '',
        // attachmentPath: null,
        // attachmentURL: null,
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        vehicleId: null,
        selectedVehicleInfo: null,
        // Don't clear attachment data when deselecting a vehicle either
        // invoiceNumber: null,
        // vendorCategory: null,
        // businessName: '',
        // attachmentPath: null,
        // attachmentURL: null,
      }))
    }
    setFormError(null)
    // Don't clear attachment file and preview when selecting/deselecting vehicles
    // setAttachmentFile(null);
    // setAttachmentPreviewURL(null);
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      category: 'Shipping',
      amount: '',
      description: '',
      date: new Date(),
      customCategory: null,
      vehicleId: null,
      transportCompanyId: null,
      selectedVehicleInfo: null,
      invoiceNumber: null,
      vendorCategory: null,
      businessName: '',
      attachmentPath: null,
      attachmentURL: null,
      repairSubcategories: [],
      customRepairSubcategory: '',
      repairPartsAmount: '',
      repairLaborAmount: '',
      repairFeesAmount: '',
      repairTaxAmount: '',
      repairShopId: null,
      paid: false,
      creditCardCharge: '',
      discountType: '',
      discountValue: '',
    })
    setFormError(null)
    setAttachmentFiles([]);
    setExistingAttachments([]);
    setAttachmentPreviewURL(null);
  }

  // Handle dialog close
  const handleClose = () => {
    resetForm()
    onClose()
  }

  // Handle expense submission
  const handleSubmit = async () => {
    // Validate form data
    let amount = formData.amount === '' ? 0 : parseFloat(formData.amount)
    const discountType: ExpenseDiscountType | null =
      formData.discountType === 'percent' || formData.discountType === 'amount'
        ? formData.discountType
        : null
    const rawDiscount =
      discountType && formData.discountValue.trim() !== '' ? parseFloat(formData.discountValue) : null
    const discountValue =
      rawDiscount != null && !Number.isNaN(rawDiscount) && rawDiscount > 0
        ? discountType === 'percent'
          ? Math.min(100, rawDiscount)
          : rawDiscount
        : null
    const creditCardCharge =
      formData.creditCardCharge.trim() === ''
        ? null
        : (() => {
            const n = parseFloat(formData.creditCardCharge)
            return Number.isFinite(n) && n > 0 ? n : null
          })()

    // For repairs, calculate from parts and labor − discount + CC
    if (formData.category === 'Repairs') {
      // Validate that at least one subcategory is selected
      if (!formData.repairSubcategories || formData.repairSubcategories.length === 0) {
        setFormError('At least one repair subcategory must be selected')
        return
      }
      
      const partsAmount = parseFloat(formData.repairPartsAmount) || 0
      const laborAmount = parseFloat(formData.repairLaborAmount) || 0
      const feesAmount = parseFloat(formData.repairFeesAmount) || 0
      const taxAmount = parseFloat(formData.repairTaxAmount) || 0
      const repairSubtotal = partsAmount + laborAmount + feesAmount + taxAmount
      
      if (repairSubtotal <= 0) {
        setFormError('At least one repair amount must be greater than 0')
        return
      }

      amount = computeExpenseAmount({
        subtotal: repairSubtotal,
        discountType,
        discountValue,
        creditCardCharge,
      })
      
      // Validate custom repair subcategory if "Other" is selected
      if (formData.repairSubcategories?.includes('Other') && !formData.customRepairSubcategory.trim()) {
        setFormError('Custom subcategory name is required when "Other" is selected')
        return
      }
    } else {
      const base = amount
      if (base <= 0) {
        setFormError('Amount must be greater than 0')
        return
      }
      amount = computeExpenseAmount({
        subtotal: base,
        discountType,
        discountValue,
        creditCardCharge,
      })
    }
    
    if (!formData.description) {
      setFormError('Description is required')
      return
    }

    try {
      const hasNewAttachments = attachmentFiles.length > 0
      const existingAttachmentPaths = existingAttachments.map((item) => item.path).filter(Boolean) as string[]
      const existingAttachmentURLs = existingAttachments.map((item) => item.url)
      // Create expense data - ensure we preserve the date format
      const expenseData = {
        // For edit mode use the existing id, otherwise use a required dummy id that will be replaced
        id: editMode && expenseToEdit ? expenseToEdit.id : '',
        category: formData.category,
        amount: amount,
        description: formData.description,
        // Preserve the date exactly as it is in the form
        date: formData.date,
        customCategory: formData.category === 'Other' ? formData.customCategory || null : null,
        vehicleId: formData.vehicleId,
        transportCompanyId: formData.category === 'Shipping' ? formData.transportCompanyId : null,
        repairSubcategories: formData.category === 'Repairs' && formData.repairSubcategories.length > 0 
          ? formData.repairSubcategories 
          : null,
        customRepairSubcategory: formData.category === 'Repairs' && formData.repairSubcategories?.includes('Other') && formData.customRepairSubcategory
          ? formData.customRepairSubcategory.trim()
          : null,
        repairPartsAmount: formData.category === 'Repairs' && formData.repairPartsAmount
          ? parseFloat(formData.repairPartsAmount) || null
          : null,
        repairLaborAmount: formData.category === 'Repairs' && formData.repairLaborAmount
          ? parseFloat(formData.repairLaborAmount) || null
          : null,
        repairFeesAmount: formData.category === 'Repairs' && formData.repairFeesAmount
          ? parseFloat(formData.repairFeesAmount) || null
          : null,
        repairTaxAmount: formData.category === 'Repairs' && formData.repairTaxAmount
          ? parseFloat(formData.repairTaxAmount) || null
          : null,
        repairShopId: formData.category === 'Repairs' ? formData.repairShopId : null,
        // Add new fields
        invoiceNumber: formData.invoiceNumber,
        vendorCategory: formData.vendorCategory,
        businessName: formData.businessName,
        attachmentPath: existingAttachmentPaths[0] ?? formData.attachmentPath ?? null,
        attachmentURL: existingAttachmentURLs[0] ?? formData.attachmentURL ?? null,
        attachmentPaths: existingAttachmentPaths.length ? existingAttachmentPaths : null,
        attachmentURLs: existingAttachmentURLs.length ? existingAttachmentURLs : null,
        paid: formData.paid,
        creditCardCharge,
        discountType,
        discountValue,
        paidAt:
          editMode && expenseToEdit && expenseToEdit.paidAt instanceof Date
            ? expenseToEdit.paidAt
            : null,
      };

      await onSubmit(expenseData, hasNewAttachments ? attachmentFiles : null);

      // Close the dialog and reset form
      handleClose()
    } catch (error) {
      console.error(`Error ${editMode ? 'updating' : 'adding'} expense:`, error)
      setFormError(`Failed to ${editMode ? 'update' : 'add'} expense. Please try again.`)
    }
  }

  // Get category color
  const getCategoryColor = (category: ExpenseCategory): 'primary' | 'error' | 'success' | 'warning' | 'info' => {
    switch (category) {
      case 'Shipping':
        return 'primary'
      case 'Repairs':
        return 'error'
      case 'RentalReimbursement':
        return 'success'
      case 'StorageFees':
        return 'info'
      case 'Other':
        return 'warning'
      default:
        return 'primary' // Default to primary instead of 'default'
    }
  }

  // Get category icon
  const getCategoryIcon = (category: ExpenseCategory) => {
    switch (category) {
      case 'Shipping':
        return <LocalShippingIcon />
      case 'Repairs':
        return <BuildIcon />
      case 'RentalReimbursement':
        return <DirectionsCarIcon />
      case 'StorageFees':
        return <WarehouseIcon />
      case 'Other':
        return <ApartmentIcon />
      default:
        return null
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth fullScreen={isMobile}>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor:
            formData.category === 'Shipping'
              ? 'primary.light'
              : formData.category === 'Repairs'
                ? 'error.light'
                : formData.category === 'RentalReimbursement'
                  ? 'success.light'
                  : formData.category === 'StorageFees'
                    ? 'info.light'
                    : formData.category === 'Other'
                      ? 'warning.light'
                      : 'primary.light',
          color: 'text.primary',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {formData.category && getCategoryIcon(formData.category)}
          <Typography variant='h6'>{editMode ? 'Edit' : 'Add New'} {formData.category} Expense</Typography>
        </Box>
        {isMobile && (
          <IconButton edge='end' color='inherit' onClick={handleClose} aria-label='close'>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent
        sx={{
          overflowY: 'auto',
          flex: 1,
          pt: isMobile ? 2 : undefined,
        }}
      >
        {formError && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        <Box component='form' sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id='category-select-label'>Category</InputLabel>
                <Select
                  labelId='category-select-label'
                  id='category-select'
                  value={formData.category}
                  label='Category'
                  name='category'
                  onChange={handleSelectChange}
                >
                  <MenuItem value='Shipping'>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalShippingIcon fontSize='small' />
                      <span>Shipping</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value='Repairs'>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BuildIcon fontSize='small' />
                      <span>Repairs</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value='RentalReimbursement'>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DirectionsCarIcon fontSize='small' />
                      <span>Rental Reimbursement</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value='StorageFees'>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarehouseIcon fontSize='small' />
                      <span>Storage Fees</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value='Other'>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ApartmentIcon fontSize='small' />
                      <span>Other</span>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.category !== 'Repairs' && (
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id='amount'
                label='Amount'
                name='amount'
                value={formData.amount}
                onChange={handleInputChange}
                placeholder='0.00'
                InputProps={{
                  startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                }}
              />
            </Grid>
            )}
            {formData.category === 'Shipping' && (
              <Grid item xs={12}>
                <Autocomplete
                  options={transporterGroups || []}
                  getOptionLabel={(group) => group.name || group.id}
                  value={transporterGroups?.find(group => group.id === formData.transportCompanyId) || null}
                  onChange={(_, newValue) =>
                    setFormData(prev => ({
                      ...prev,
                      transportCompanyId: newValue?.id || null
                    }))
                  }
                  loading={transporterGroupsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Transport Company (Optional)"
                      placeholder="Search transport companies..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <LocalShippingIcon fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, group) => (
                    <Box component="li" {...props} sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LocalShippingIcon color="action" fontSize="small" />
                        <Typography variant="body2">{group.name}</Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>
            )}
            {formData.category === 'Repairs' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id='repairPartsAmount'
                    label='Parts Amount'
                    name='repairPartsAmount'
                    type='number'
                    value={formData.repairPartsAmount}
                    onChange={handleInputChange}
                    placeholder='0.00'
                    InputProps={{
                      startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                      inputProps: { min: 0, step: 0.01 },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id='repairLaborAmount'
                    label='Labor Amount'
                    name='repairLaborAmount'
                    type='number'
                    value={formData.repairLaborAmount}
                    onChange={handleInputChange}
                    placeholder='0.00'
                    InputProps={{
                      startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                      inputProps: { min: 0, step: 0.01 },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id='repairFeesAmount'
                    label='Fees'
                    name='repairFeesAmount'
                    type='number'
                    value={formData.repairFeesAmount}
                    onChange={handleInputChange}
                    placeholder='0.00'
                    InputProps={{
                      startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                      inputProps: { min: 0, step: 0.01 },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id='repairTaxAmount'
                    label='Tax'
                    name='repairTaxAmount'
                    type='number'
                    value={formData.repairTaxAmount}
                    onChange={handleInputChange}
                    placeholder='0.00'
                    InputProps={{
                      startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                      inputProps: { min: 0, step: 0.01 },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Total: {formatPrice(
                      computeExpenseAmount({
                        subtotal:
                          (parseFloat(formData.repairPartsAmount) || 0) +
                          (parseFloat(formData.repairLaborAmount) || 0) +
                          (parseFloat(formData.repairFeesAmount) || 0) +
                          (parseFloat(formData.repairTaxAmount) || 0),
                        discountType:
                          formData.discountType === 'percent' || formData.discountType === 'amount'
                            ? formData.discountType
                            : null,
                        discountValue:
                          formData.discountValue.trim() === ''
                            ? null
                            : parseFloat(formData.discountValue) || null,
                        creditCardCharge:
                          formData.creditCardCharge.trim() === ''
                            ? null
                            : parseFloat(formData.creditCardCharge) || null,
                      }),
                    )}
                  </Typography>
                </Grid>
              </>
            )}
            {formData.category === 'Other' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id='customCategory'
                  label='Custom Category'
                  name='customCategory'
                  value={formData.customCategory || ''}
                  onChange={handleInputChange}
                />
              </Grid>
            )}
            {formData.category === 'Repairs' && (
              <Grid item xs={12}>
                <RepairCategoryPicker
                  selected={formData.repairSubcategories ?? []}
                  onChange={next =>
                    setFormData(prev => ({
                      ...prev,
                      repairSubcategories: next as RepairSubcategory[],
                      // Drop the legacy custom name when "Other" is no longer selected
                      customRepairSubcategory: next.includes('Other')
                        ? prev.customRepairSubcategory
                        : '',
                    }))
                  }
                  categories={repairCategories}
                  subcategories={customSubcategories}
                  onManage={() => setTaxonomyDialogOpen(true)}
                />
              </Grid>
            )}
            {formData.category === 'Repairs' && formData.repairSubcategories?.includes('Other') && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id='customRepairSubcategory'
                  label='Custom Subcategory Name'
                  name='customRepairSubcategory'
                  value={formData.customRepairSubcategory || ''}
                  onChange={handleInputChange}
                  required
                  error={!!formError && formError.includes('Custom subcategory')}
                  helperText={formError && formError.includes('Custom subcategory') ? formError : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CategoryIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
            {formData.category === 'Repairs' && (
              <Grid item xs={12}>
                <Autocomplete
                  options={repairShopPickerOptions}
                  getOptionLabel={(shop) => {
                    if (!shop.name) {
                      return `${shop.address}, ${shop.city}, ${shop.state} ${shop.zip}`
                    }
                    return `${shop.name} - ${shop.address}, ${shop.city}, ${shop.state} ${shop.zip}`
                  }}
                  value={repairShopLocations?.find(shop => shop.id === formData.repairShopId) || null}
                  onChange={(_, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      repairShopId: newValue?.id || null
                    }))
                  }}
                  loading={repairShopLocationsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Repair Shop (Optional)"
                      placeholder="Search repair shops..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <BuildIcon fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, shop) => (
                    <Box component="li" {...props} sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <BuildIcon color="action" fontSize="small" />
                        <Box>
                          {shop.name && (
                            <Typography variant="body2" fontWeight="bold">
                              {shop.name}
                              {shop.is_active === false ? ' (inactive)' : ''}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            {shop.address}, {shop.city}, {shop.state} {shop.zip}
                          </Typography>
                          {shop.contact_name && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Contact: {shop.contact_name}
                            </Typography>
                          )}
                          {shop.contact_phone && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Phone: {shop.contact_phone}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeInactiveRepairShops}
                      onChange={(e) => setIncludeInactiveRepairShops(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Include inactive shops"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id='description'
                label='Description'
                name='description'
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={isMobile ? 3 : 2}
              />
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label='Date'
                  value={dayjs(formData.date)}
                  onChange={handleDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            {/* New fields section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="Vendor Information" />
              </Divider>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                id='businessName'
                label='Business Name'
                name='businessName'
                value={formData.businessName}
                onChange={handleInputChange}
                placeholder='Optional'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <BusinessIcon fontSize='small' />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id='invoiceNumber'
                label='Invoice Number'
                name='invoiceNumber'
                value={formData.invoiceNumber || ''}
                onChange={handleInputChange}
                placeholder='Optional'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <ReceiptIcon fontSize='small' />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id='vendorCategory'
                label='Vendor Category'
                name='vendorCategory'
                value={formData.vendorCategory || ''}
                onChange={handleInputChange}
                placeholder='Optional'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <CategoryIcon fontSize='small' />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="Payment" />
              </Divider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.paid}
                    onChange={e => setFormData(prev => ({ ...prev, paid: e.target.checked }))}
                  />
                }
                label="Paid"
              />
            </Grid>
            {editMode && expenseToEdit && expenseToEdit.paidAt instanceof Date && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Marked paid: {expenseToEdit.paidAt.toLocaleDateString('en-US', { timeZone: 'America/New_York' })}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="creditCardCharge"
                label="Credit card charge"
                name="creditCardCharge"
                value={formData.creditCardCharge}
                onChange={handleInputChange}
                placeholder="Optional"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="legacy-discount-type-label">Discount type</InputLabel>
                <Select
                  labelId="legacy-discount-type-label"
                  label="Discount type"
                  name="discountType"
                  value={formData.discountType}
                  onChange={handleSelectChange}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="percent">Percent</MenuItem>
                  <MenuItem value="amount">Amount</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="discountValue"
                label={formData.discountType === 'percent' ? 'Discount (%)' : 'Discount amount'}
                name="discountValue"
                value={formData.discountValue}
                onChange={handleInputChange}
                placeholder="Optional"
                disabled={!formData.discountType}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {formData.discountType === 'percent' ? '%' : '$'}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {/* Attachment section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="Attachment" />
              </Divider>
            </Grid>
            
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<AttachFileIcon />}
                disabled={isExtracting}
              >
                {attachmentFiles.length > 0
                  ? `${attachmentFiles.length} file${attachmentFiles.length === 1 ? '' : 's'} selected`
                  : existingAttachments.length > 0
                    ? `Replace attachments (${existingAttachments.length})`
                    : 'Upload Attachments'}
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                />
              </Button>
              {attachmentFiles.length > 0 && !isExtracting && (
                <Button 
                  onClick={handleExtractInvoiceData} 
                  variant="contained" 
                  color="secondary" 
                  fullWidth 
                  sx={{ mt: 1 }} 
                  startIcon={<PsychologyIcon />}
                >
                  Extract Info
                </Button>
              )}
              {isExtracting && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, justifyContent: 'center' }}>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  <Typography>Extracting invoice data...</Typography>
                </Box>
              )}
              {(attachmentFiles.length > 0 || existingAttachments.length > 0) && (
                <Box mt={1}>
                  {attachmentFiles.length > 0 ? (
                    attachmentFiles.map((file) => (
                      <Typography key={file.name} variant="body2">{file.name}</Typography>
                    ))
                  ) : (
                    existingAttachments.map((attachment) => (
                      <Typography key={attachment.url} variant="body2">
                        <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                          {attachment.path?.split('/').pop() || attachment.url.split('/').pop()?.split('?')[0] || 'Attachment'}
                        </a>
                      </Typography>
                    ))
                  )}
                </Box>
              )}
              {attachmentPreviewURL && !isExtracting && (
                <Box mt={2} textAlign="center">
                  {attachmentFiles[0]?.type.startsWith('image/') || attachmentPreviewURL?.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                    <img src={attachmentPreviewURL} alt="Attachment Preview" style={{ maxHeight: '200px', maxWidth: '100%', borderRadius: '4px' }} />
                  ) : existingAttachments.length > 0 || attachmentFiles.length > 0 ? (
                    <Typography variant="body2">
                      {attachmentFiles.length > 0 ? attachmentFiles[0].name : (
                        <a href={existingAttachments[0]?.url || '#'} target="_blank" rel="noopener noreferrer">
                          {existingAttachments[0]?.path?.split('/').pop() || existingAttachments[0]?.url?.split('/').pop()?.split('?')[0]}
                        </a>
                      )}
                    </Typography>
                  ) : null}
                </Box>
              )}
            </Grid>
            
            {/* Vehicle linking section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="Vehicle Information" />
              </Divider>
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                options={vehicles || []}
                value={formData.selectedVehicleInfo}
                getOptionLabel={(option) =>
                  `${option.stock_number || 'N/A'} - ${option.year} ${option.make} ${option.model} (${option.vin || 'VIN N/A'})`
                }
                filterOptions={(options, { inputValue }) =>
                  filterVehicleLinkOptions(options, inputValue)
                }
                onChange={handleVehicleChange}
                renderOption={(props, option) => (
                  <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                    <Grid container direction="column">
                      <Grid item>
                        <Typography variant="body1">
                          {option.year} {option.make} {option.model}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="caption" color="text.secondary">
                          Stock #: {option.stock_number || 'N/A'} | VIN: {option.vin || 'N/A'} 
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="caption" color="text.secondary">
                          Size: {option.size || 'N/A'}
                          {option.status === 'Sold' && option.sold_date && (
                            <Chip
                              label={`Sold: ${dayjs(option.sold_date).format('MMM D, YYYY')}`}
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Link Vehicle (Stock # or VIN)"
                    variant="outlined"
                    helperText={!vehiclesLoading && !formData.selectedVehicleInfo ? "Search by VIN, Stock #, Make, or Model" : ""}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          {vehiclesLoading ? <CircularProgress color="inherit" size={20} /> : <LinkIcon color="primary" sx={{ mr: 1 }} />}
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                fullWidth
                sx={{ mb: 2 }}
              />
              {formData.selectedVehicleInfo && (
                <Paper elevation={0} sx={{ p: 1.5, mb: 2, bgcolor: 'grey.100' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Vehicle:
                  </Typography>
                  <Typography variant="body2">
                    {formData.selectedVehicleInfo.year} {formData.selectedVehicleInfo.make} {formData.selectedVehicleInfo.model}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Stock #: {formData.selectedVehicleInfo.stock_number || 'N/A'} | VIN: {formData.selectedVehicleInfo.vin || 'N/A'} | Size: {formData.selectedVehicleInfo.size || 'N/A'}
                  </Typography>
                  {formData.selectedVehicleInfo.status === 'Sold' && formData.selectedVehicleInfo.sold_date && (
                    <Chip
                      label={`Sold: ${dayjs(formData.selectedVehicleInfo.sold_date).format('MMM D, YYYY')}`}
                      size="small"
                      color="success"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Paper>
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          p: isMobile ? 2 : undefined,
          flexDirection: isMobile ? 'column' : 'row',
          '& > button': isMobile
            ? {
                width: '100%',
                mb: 1,
                py: 1.5,
              }
            : {},
        }}
      >
        {isMobile ? (
          <>
            <Button onClick={handleSubmit} variant='contained' color='primary' fullWidth size='large'>
              {editMode ? 'Update' : 'Add'} Expense
            </Button>
            <Button onClick={handleClose} variant='outlined' fullWidth>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSubmit} variant='contained' color='primary'>
              {editMode ? 'Update' : 'Add'} Expense
            </Button>
          </>
        )}
      </DialogActions>
      <RepairTaxonomyDialog
        open={taxonomyDialogOpen}
        onClose={() => setTaxonomyDialogOpen(false)}
      />
    </Dialog>
  )
}

export default ExpenseDialog;